/**
 * React Query hooks + types for the Connect Admin tool.
 *
 * Connect is the wholesale / broker partner-portal administration surface.
 * Every endpoint lives under /v1/connect/* on the shared API. Binary
 * access-grant model (spec 00_access-binary-grants.md, 2026-08-09): each of
 * the four Connect tools is its own accessSlug, no levels — holding the tool
 * grants all of its APIs. Replaces the old connect:read / :write / :admin
 * levels, which never distinguished Partners from Pricing from Agreements
 * from Adoption.
 *
 * Graceful degradation: several Connect backends are being built in parallel.
 * A 404 (route not deployed) or 503 (feature not configured) is treated as a
 * quiet "not available yet" state by the pages, never a crash. The hooks pass
 * the error through; callers use `isNotAvailable(error)` to detect it.
 *
 * Mirrors the react-query patterns in useCrm.ts / useCoas.ts / useNetmon.ts.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, type ApiError } from './api';
import { API_BASE } from './apiBase';

export const CONNECT_API_BASE = `${API_BASE}/v1/connect`;

// --------------------------------------------------------------------------
// RBAC
// --------------------------------------------------------------------------

export function hasConnectPartners(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('connect.partners');
}

export function hasConnectPricing(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('connect.pricing');
}

export function hasConnectAgreements(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('connect.agreements');
}

export function hasConnectAdoption(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('connect.adoption');
}

// A 404 (route not yet deployed) or 503 (feature not configured) means the
// backend for this slice is not live yet. Pages render a quiet placeholder
// rather than an error.
export function isNotAvailable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === 404 || status === 503;
}

// --------------------------------------------------------------------------
// Shared types
// --------------------------------------------------------------------------

export type OrgType = 'wholesaler' | 'broker';
export type OrgStatus = string; // backend-defined; commonly active / inactive / prospect
export type PricingBucket = 'wholesale' | 'distributor';
export type FreightBasis = 'fob' | 'delivered';

export type SageCustomerRef = {
  division_no: string;
  customer_no: string;
  is_primary: boolean;
  // The API is adding the Sage customer name alongside the number so the UI can
  // show "0223249 · Chaloandco" instead of a bare id. Optional so a payload
  // that predates that change (or a plain-string element) still parses.
  name?: string | null;
};

// Normalize one sage_customers element into a SageCustomerRef. The API may
// serve either the object shape ({division_no, customer_no, is_primary, name?})
// or, in an older/simpler shape, a plain customer-number string. Tolerate both
// so the UI renders regardless of which side deployed first.
export function normalizeSageCustomerRef(raw: unknown): SageCustomerRef {
  if (typeof raw === 'string') {
    return { division_no: '', customer_no: raw, is_primary: false, name: null };
  }
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    division_no: typeof o['division_no'] === 'string' ? (o['division_no'] as string) : '',
    customer_no:
      o['customer_no'] != null ? String(o['customer_no']) : o['CustomerNo'] != null ? String(o['CustomerNo']) : '',
    is_primary: !!o['is_primary'],
    name:
      o['name'] != null
        ? String(o['name'])
        : o['CustomerName'] != null
          ? String(o['CustomerName'])
          : null,
  };
}

// Plain-English label for a mapped Sage customer: "0223249 · Chaloandco" when
// the name is present, otherwise just the number.
export function sageCustomerLabel(c: SageCustomerRef): string {
  const name = c.name?.trim();
  return name ? `${c.customer_no} · ${name}` : c.customer_no;
}

export type SalespersonCodeRef = {
  division_no: string;
  salesperson_no: string;
  // Human name from the Sage salesperson master. null when unknown / not yet
  // synced; optional so a UI built after the API change still reads pre-change
  // responses that omit the field.
  name?: string | null;
  commission_rate: number | null;
};

// Plain-English label for a salesperson code: "BL - Bob Lawson" when the name
// is present, otherwise just the code. Accepts either the mapping ref or the
// picker stat (both carry salesperson_no + optional name).
export function salespersonCodeLabel(c: { salesperson_no: string; name?: string | null }): string {
  const name = c.name?.trim();
  return name ? `${c.salesperson_no} - ${name}` : c.salesperson_no;
}

export type ConnectOrg = {
  id: number;
  name: string;
  org_type: OrgType;
  status: OrgStatus;
  pricing_bucket: PricingBucket | null;
  freight_basis: FreightBasis | null;
  sage_customers: SageCustomerRef[];
  salesperson_codes: SalespersonCodeRef[];
  user_count: number;
  created_at: string;
};

export type ConnectUser = {
  id: number;
  email: string;
  name: string | null;
  role: string; // 'org-admin' | 'buyer'
  status: string; // 'active' | 'disabled'
  last_login_at: string | null;
  created_at: string;
};

export type ConnectAgreementStatus =
  | 'draft'
  | 'sent'
  | 'signed'
  | 'declined'
  | 'voided'
  | 'expired';

export type ConnectAgreement = {
  id: number;
  org_id: number;
  org_name: string;
  title: string;
  agreement_type: string | null;
  status: ConnectAgreementStatus;
  docusign_envelope_id: string | null;
  signer_email: string | null;
  document_url: string | null;
  sent_at: string | null;
  signed_at: string | null;
  expires_at: string | null;
  created_by: string | null;
};

export type ConnectActivity = {
  actor_email: string;
  action: string;
  detail: string | null;
  created_at: string;
};

export type ConnectRecentOrder = {
  id?: number;
  source_order_id?: string;
  sage_so_number?: string | null;
  status?: string;
  total_amount?: string | number | null;
  order_date?: string | null;
  [key: string]: unknown;
};

export type ConnectOrgDetail = {
  org: ConnectOrg;
  users: ConnectUser[];
  agreements: ConnectAgreement[];
  recent_orders: ConnectRecentOrder[];
  activity: ConnectActivity[];
  // Present only for broker orgs (served inline so the detail page needs no
  // second round-trip). Loose-typed; the Commissions tab reads it opportunistically.
  commission_statement?: unknown;
};

// --------------------------------------------------------------------------
// Orgs (partners)
// --------------------------------------------------------------------------

export type OrgFilters = {
  q?: string;
  org_type?: OrgType | '';
  status?: string;
};

type OrgsResponse = { orgs: ConnectOrg[] };

export function useConnectOrgs(filters: OrgFilters) {
  const query = useQuery<OrgsResponse, Error>({
    queryKey: ['connect', 'orgs', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.q) qs.set('q', filters.q);
      if (filters.org_type) qs.set('org_type', filters.org_type);
      if (filters.status) qs.set('status', filters.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiGet<OrgsResponse>(`${CONNECT_API_BASE}/orgs${suffix}`);
    },
    staleTime: 15_000,
    retry: false,
  });
  return {
    orgs: query.data?.orgs ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

// Normalize the org-detail response into { org, users, agreements, ... }.
//
// The API serves the org's own fields spread at the TOP LEVEL (id, name,
// org_type, sage_customers, ...) with users/agreements/recent_orders/activity
// as siblings, not wrapped under an `org` key. The page reads `data.org`, so a
// raw pass-through left `org` undefined and the page rendered blank. This maps
// either shape (flat top-level, or an already-wrapped `{ org: {...} }`) into the
// wrapped shape the page expects, and normalizes each sage_customers element so
// the mapping editor can show the customer name when the API provides it.
export function normalizeOrgDetail(raw: unknown): ConnectOrgDetail {
  const r = (raw ?? {}) as Record<string, unknown>;
  const orgSource = (r['org'] && typeof r['org'] === 'object' ? r['org'] : r) as Record<string, unknown>;

  const rawCustomers = Array.isArray(orgSource['sage_customers']) ? (orgSource['sage_customers'] as unknown[]) : [];
  const rawCodes = Array.isArray(orgSource['salesperson_codes'])
    ? (orgSource['salesperson_codes'] as SalespersonCodeRef[])
    : [];

  const org: ConnectOrg = {
    id: Number(orgSource['id'] ?? 0),
    name: String(orgSource['name'] ?? ''),
    org_type: (orgSource['org_type'] === 'broker' ? 'broker' : 'wholesaler') as OrgType,
    status: String(orgSource['status'] ?? ''),
    pricing_bucket: (orgSource['pricing_bucket'] as PricingBucket | null) ?? null,
    freight_basis: (orgSource['freight_basis'] as FreightBasis | null) ?? null,
    sage_customers: rawCustomers.map(normalizeSageCustomerRef),
    salesperson_codes: rawCodes,
    user_count: Number(orgSource['user_count'] ?? 0),
    created_at: String(orgSource['created_at'] ?? ''),
  };

  return {
    org,
    users: Array.isArray(r['users']) ? (r['users'] as ConnectUser[]) : [],
    agreements: Array.isArray(r['agreements']) ? (r['agreements'] as ConnectAgreement[]) : [],
    recent_orders: Array.isArray(r['recent_orders']) ? (r['recent_orders'] as ConnectRecentOrder[]) : [],
    activity: Array.isArray(r['activity']) ? (r['activity'] as ConnectActivity[]) : [],
    commission_statement: r['commission_statement'],
  };
}

export function useConnectOrg(
  id: string | number | undefined,
): UseQueryResult<ConnectOrgDetail, Error> {
  return useQuery<ConnectOrgDetail, Error>({
    queryKey: ['connect', 'org', String(id ?? '')],
    queryFn: async () => {
      const raw = await apiGet<unknown>(`${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(id))}`);
      return normalizeOrgDetail(raw);
    },
    enabled: id != null && String(id).length > 0,
    staleTime: 10_000,
    retry: false,
  });
}

export type CreateOrgInput = {
  name: string;
  org_type: OrgType;
  status?: string;
  pricing_bucket?: PricingBucket;
  freight_basis?: FreightBasis;
};

export function useCreateConnectOrg() {
  const qc = useQueryClient();
  return useMutation<ConnectOrg, Error, CreateOrgInput>({
    mutationFn: (input) => apiPost<ConnectOrg>(`${CONNECT_API_BASE}/orgs`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'orgs'] });
    },
  });
}

export type PatchOrgInput = Partial<{
  name: string;
  status: string;
  pricing_bucket: PricingBucket;
  freight_basis: FreightBasis;
  org_type: OrgType;
}>;

export function usePatchConnectOrg(id: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<ConnectOrg, Error, PatchOrgInput>({
    mutationFn: (input) =>
      apiPatch<ConnectOrg>(`${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(id))}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(id ?? '')] });
      void qc.invalidateQueries({ queryKey: ['connect', 'orgs'] });
    },
  });
}

export type PutCustomersInput = {
  customers: { division_no?: string; customer_no: string; is_primary?: boolean }[];
};

export function usePutOrgCustomers(id: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, PutCustomersInput>({
    mutationFn: (input) =>
      apiPut(`${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(id))}/customers`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(id ?? '')] });
      void qc.invalidateQueries({ queryKey: ['connect', 'orgs'] });
    },
  });
}

export type PutSalespersonsInput = {
  codes: { division_no?: string; salesperson_no: string; commission_rate?: number }[];
};

export function usePutOrgSalespersons(id: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, PutSalespersonsInput>({
    mutationFn: (input) =>
      apiPut(`${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(id))}/salespersons`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(id ?? '')] });
      void qc.invalidateQueries({ queryKey: ['connect', 'orgs'] });
    },
  });
}

// --------------------------------------------------------------------------
// Users
// --------------------------------------------------------------------------

type UsersResponse = { users: ConnectUser[] };

export function useConnectOrgUsers(id: string | number | undefined) {
  return useQuery<UsersResponse, Error>({
    queryKey: ['connect', 'org-users', String(id ?? '')],
    queryFn: () =>
      apiGet<UsersResponse>(`${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(id))}/users`),
    enabled: id != null && String(id).length > 0,
    staleTime: 10_000,
    retry: false,
  });
}

export type CreateUserInput = { email: string; name?: string; role: string };

export function useCreateConnectUser(orgId: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<ConnectUser, Error, CreateUserInput>({
    mutationFn: (input) =>
      apiPost<ConnectUser>(
        `${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(orgId))}/users`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'org-users', String(orgId ?? '')] });
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(orgId ?? '')] });
    },
  });
}

export type PatchUserInput = Partial<{ role: string; status: 'active' | 'disabled' }>;

export function usePatchConnectUser(orgId: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<ConnectUser, Error, { userId: number; body: PatchUserInput }>({
    mutationFn: ({ userId, body }) =>
      apiPatch<ConnectUser>(`${CONNECT_API_BASE}/users/${userId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'org-users', String(orgId ?? '')] });
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(orgId ?? '')] });
    },
  });
}

export function useResetConnectUserPassword() {
  return useMutation<{ status: string }, Error, number>({
    mutationFn: (userId) =>
      apiPost<{ status: string }>(`${CONNECT_API_BASE}/users/${userId}/reset-password`, {}),
  });
}

// --------------------------------------------------------------------------
// Impersonate (staff-only "open portal as this partner")
// --------------------------------------------------------------------------
// POST /v1/connect/orgs/{id}/impersonate mints a short-lived (1 hour) staff
// view of the partner's portal and returns a token + a ready-to-open portal_url.
// The endpoint is being deployed in parallel; while it 404s, the button falls
// back to a quiet disabled state (isNotAvailable).

export type ImpersonateResponse = { token: string; portal_url: string };

export function useImpersonateOrg(orgId: string | number | undefined) {
  return useMutation<ImpersonateResponse, Error, void>({
    mutationFn: () =>
      apiPost<ImpersonateResponse>(
        `${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(orgId))}/impersonate`,
        {},
      ),
  });
}

// --------------------------------------------------------------------------
// Prices
// --------------------------------------------------------------------------

export type ConnectPrice = {
  item_code: string;
  category: string | null;
  description: string | null;
  first_cost: number | string | null;
  fob_wholesale: number | string | null;
  delivered_wholesale: number | string | null;
  fob_distributor: number | string | null;
  delivered_distributor: number | string | null;
  effective_date: string | null;
  loaded_at: string | null;
};

export const PRICE_FIELDS = [
  'first_cost',
  'fob_wholesale',
  'delivered_wholesale',
  'fob_distributor',
  'delivered_distributor',
] as const;
export type PriceField = (typeof PRICE_FIELDS)[number];

type PricesResponse = { prices: ConnectPrice[]; count: number };

export function useConnectPrices(q: string) {
  const query = useQuery<PricesResponse, Error>({
    queryKey: ['connect', 'prices', q],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiGet<PricesResponse>(`${CONNECT_API_BASE}/prices${suffix}`);
    },
    staleTime: 15_000,
    retry: false,
  });
  return {
    prices: query.data?.prices ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

export type PatchPriceInput = Partial<Record<PriceField, number | null>> &
  Partial<{ category: string; description: string; effective_date: string }>;

export function usePatchConnectPrice() {
  const qc = useQueryClient();
  return useMutation<ConnectPrice, Error, { itemCode: string; body: PatchPriceInput }>({
    mutationFn: ({ itemCode, body }) =>
      apiPatch<ConnectPrice>(`${CONNECT_API_BASE}/prices/${encodeURIComponent(itemCode)}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'prices'] });
    },
  });
}

export type PriceImportRow = {
  item_code: string;
  category?: string;
  description?: string;
  first_cost?: number | string | null;
  fob_wholesale?: number | string | null;
  delivered_wholesale?: number | string | null;
  fob_distributor?: number | string | null;
  delivered_distributor?: number | string | null;
};

export type PriceImportChange = {
  item_code: string;
  field: string;
  old: number | string | null;
  new: number | string | null;
};

export type PriceImportResult = {
  new: PriceImportRow[];
  changed: PriceImportChange[];
  removed: PriceImportRow[];
  duplicates: PriceImportRow[];
};

export function useImportConnectPrices() {
  const qc = useQueryClient();
  return useMutation<
    PriceImportResult,
    Error,
    { mode: 'preview' | 'apply'; rows: PriceImportRow[] }
  >({
    mutationFn: ({ mode, rows }) =>
      apiPost<PriceImportResult>(`${CONNECT_API_BASE}/prices/import`, { mode, rows }),
    onSuccess: (_data, vars) => {
      if (vars.mode === 'apply') {
        void qc.invalidateQueries({ queryKey: ['connect', 'prices'] });
      }
    },
  });
}

export type PriceHistoryRow = {
  item_code: string;
  field: string;
  old_value: number | string | null;
  new_value: number | string | null;
  changed_by: string | null;
  changed_at: string | null;
};

export function useConnectPriceHistory(itemCode: string | undefined) {
  return useQuery<{ history: PriceHistoryRow[] }, Error>({
    queryKey: ['connect', 'price-history', itemCode ?? ''],
    queryFn: () =>
      apiGet<{ history: PriceHistoryRow[] }>(
        `${CONNECT_API_BASE}/prices/history?item_code=${encodeURIComponent(itemCode ?? '')}`,
      ),
    enabled: !!itemCode,
    staleTime: 30_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Agreements
// --------------------------------------------------------------------------

export type AgreementFilters = { org_id?: string | number; status?: string };

type AgreementsResponse = { agreements: ConnectAgreement[] };

export function useConnectAgreements(filters: AgreementFilters) {
  const query = useQuery<AgreementsResponse, Error>({
    queryKey: ['connect', 'agreements', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.org_id != null && String(filters.org_id).length > 0) {
        qs.set('org_id', String(filters.org_id));
      }
      if (filters.status) qs.set('status', filters.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiGet<AgreementsResponse>(`${CONNECT_API_BASE}/agreements${suffix}`);
    },
    staleTime: 15_000,
    retry: false,
  });
  return {
    agreements: query.data?.agreements ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

export type CreateAgreementInput = {
  org_id: number;
  title: string;
  agreement_type?: string;
  status?: ConnectAgreementStatus;
  signer_email?: string;
  document_url?: string;
  sent_at?: string;
  expires_at?: string;
};

export function useCreateConnectAgreement() {
  const qc = useQueryClient();
  return useMutation<ConnectAgreement, Error, CreateAgreementInput>({
    mutationFn: (input) => apiPost<ConnectAgreement>(`${CONNECT_API_BASE}/agreements`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['connect', 'agreements'] });
      void qc.invalidateQueries({ queryKey: ['connect', 'org', String(vars.org_id)] });
    },
  });
}

export type PatchAgreementInput = Partial<{
  title: string;
  agreement_type: string;
  status: ConnectAgreementStatus;
  signer_email: string;
  document_url: string;
  sent_at: string;
  signed_at: string;
  expires_at: string;
  docusign_envelope_id: string;
}>;

export function usePatchConnectAgreement() {
  const qc = useQueryClient();
  return useMutation<ConnectAgreement, Error, { id: number; body: PatchAgreementInput }>({
    mutationFn: ({ id, body }) =>
      apiPatch<ConnectAgreement>(`${CONNECT_API_BASE}/agreements/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'agreements'] });
      void qc.invalidateQueries({ queryKey: ['connect', 'org'] });
    },
  });
}

// --------------------------------------------------------------------------
// Commission payments + statement
// --------------------------------------------------------------------------

export type CommissionPayment = {
  id: number;
  org_id: number;
  period_start: string;
  period_end: string;
  amount: number | string;
  paid_at: string | null;
  reference: string | null;
  note: string | null;
};

export function useConnectCommissionPayments(orgId: string | number | undefined) {
  return useQuery<{ payments: CommissionPayment[] }, Error>({
    queryKey: ['connect', 'commission-payments', String(orgId ?? '')],
    queryFn: () =>
      apiGet<{ payments: CommissionPayment[] }>(
        `${CONNECT_API_BASE}/commission-payments?org_id=${encodeURIComponent(String(orgId))}`,
      ),
    enabled: orgId != null && String(orgId).length > 0,
    staleTime: 15_000,
    retry: false,
  });
}

export type CreateCommissionPaymentInput = {
  org_id: number;
  period_start: string;
  period_end: string;
  amount: number;
  paid_at?: string;
  reference?: string;
  note?: string;
};

export function useCreateCommissionPayment() {
  const qc = useQueryClient();
  return useMutation<CommissionPayment, Error, CreateCommissionPaymentInput>({
    mutationFn: (input) =>
      apiPost<CommissionPayment>(`${CONNECT_API_BASE}/commission-payments`, input),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({
        queryKey: ['connect', 'commission-payments', String(vars.org_id)],
      });
      void qc.invalidateQueries({ queryKey: ['connect', 'commission-statement', String(vars.org_id)] });
    },
  });
}

export function usePatchCommissionPayment(orgId: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<
    CommissionPayment,
    Error,
    { id: number; body: Partial<CreateCommissionPaymentInput> }
  >({
    mutationFn: ({ id, body }) =>
      apiPatch<CommissionPayment>(`${CONNECT_API_BASE}/commission-payments/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['connect', 'commission-payments', String(orgId ?? '')],
      });
      void qc.invalidateQueries({
        queryKey: ['connect', 'commission-statement', String(orgId ?? '')],
      });
    },
  });
}

export function useDeleteCommissionPayment(orgId: string | number | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => apiDelete(`${CONNECT_API_BASE}/commission-payments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['connect', 'commission-payments', String(orgId ?? '')],
      });
      void qc.invalidateQueries({
        queryKey: ['connect', 'commission-statement', String(orgId ?? '')],
      });
    },
  });
}

export type CommissionStatementMonth = {
  month: string;
  sales: number | string;
  accrued_commission: number | string;
};

export type CommissionStatement = {
  months: CommissionStatementMonth[];
  payments: CommissionPayment[];
  // balance fields are backend-defined; keep them loose so extra fields render.
  total_accrued?: number | string;
  total_paid?: number | string;
  balance?: number | string;
  [key: string]: unknown;
};

export function useConnectCommissionStatement(orgId: string | number | undefined) {
  return useQuery<CommissionStatement, Error>({
    queryKey: ['connect', 'commission-statement', String(orgId ?? '')],
    queryFn: () =>
      apiGet<CommissionStatement>(
        `${CONNECT_API_BASE}/orgs/${encodeURIComponent(String(orgId))}/commission-statement`,
      ),
    enabled: orgId != null && String(orgId).length > 0,
    staleTime: 15_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Catalog overrides
// --------------------------------------------------------------------------

export type CatalogOverrideAction = 'hide' | 'feature' | 'include';

export type CatalogOverride = {
  id: number;
  brand: string;
  item_code: string;
  action: CatalogOverrideAction;
  note: string | null;
  created_by: string | null;
  created_at: string | null;
};

export function useConnectCatalogOverrides() {
  const query = useQuery<{ overrides: CatalogOverride[] }, Error>({
    queryKey: ['connect', 'catalog-overrides'],
    queryFn: () =>
      apiGet<{ overrides: CatalogOverride[] }>(`${CONNECT_API_BASE}/catalog-overrides`),
    staleTime: 15_000,
    retry: false,
  });
  return {
    overrides: query.data?.overrides ?? [],
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

export type UpsertCatalogOverrideInput = {
  brand: string;
  item_code: string;
  action: CatalogOverrideAction;
  note?: string;
};

export function useUpsertCatalogOverride() {
  const qc = useQueryClient();
  return useMutation<CatalogOverride, Error, UpsertCatalogOverrideInput>({
    mutationFn: (input) => apiPost<CatalogOverride>(`${CONNECT_API_BASE}/catalog-overrides`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'catalog-overrides'] });
    },
  });
}

export function useDeleteCatalogOverride() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => apiDelete(`${CONNECT_API_BASE}/catalog-overrides/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connect', 'catalog-overrides'] });
    },
  });
}

// --------------------------------------------------------------------------
// Salesperson codes (broker code picker source)
// --------------------------------------------------------------------------

export type SalespersonCodeStat = {
  salesperson_no: string;
  // Human name from the Sage salesperson master. null when unknown / not yet
  // synced; optional so the UI tolerates a pre-change API response.
  name?: string | null;
  customer_count: number;
  invoice_count: number;
};

export function useConnectSalespersonCodes() {
  const query = useQuery<{ codes: SalespersonCodeStat[] }, Error>({
    queryKey: ['connect', 'salesperson-codes'],
    queryFn: () =>
      apiGet<{ codes: SalespersonCodeStat[] }>(`${CONNECT_API_BASE}/salesperson-codes`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { codes: query.data?.codes ?? [], isLoading: query.isLoading, error: query.error };
}

// --------------------------------------------------------------------------
// Adoption dashboard
// --------------------------------------------------------------------------

export type AdoptionOrg = {
  id: number;
  name: string;
  org_type: OrgType;
  status: string;
  users: number;
  last_login_at: string | null;
  orders_30d: number;
  orders_total: number;
  never_signed_in: boolean;
};

export type AdoptionResponse = {
  orgs: AdoptionOrg[];
  totals: { orgs: number; active_users: number; orders_30d: number };
};

export function useConnectAdoption() {
  return useQuery<AdoptionResponse, Error>({
    queryKey: ['connect', 'adoption'],
    queryFn: () => apiGet<AdoptionResponse>(`${CONNECT_API_BASE}/adoption`),
    staleTime: 30_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Sage customer search (reuses the EXISTING commerce customer-search endpoint)
// --------------------------------------------------------------------------
// GET /v1/commerce/customers/search?q=&state=&limit= -> { customers: [...] }.
// Contract confirmed from tools/commerce/src/commerce-read.ts (searchCustomers).
// Requires the caller to hold commerce:read. Rows are Sage AR columns in
// PascalCase; there is no division_no on this endpoint, so the mapping picker
// treats division_no as optional (backend defaults it when the PUT omits it).

export const COMMERCE_API_BASE = `${API_BASE}/v1/commerce`;

export type SageCustomerSearchRow = {
  CustomerNo: string;
  CustomerName: string;
  AddressLine1: string;
  City: string;
  State: string;
  ZipCode: string;
  TelephoneNo: string;
};

export function useSageCustomerSearch(q: string) {
  const trimmed = q.trim();
  const enabled = trimmed.length >= 2;
  const query = useQuery<{ customers: SageCustomerSearchRow[] }, Error>({
    queryKey: ['connect', 'customer-search', trimmed],
    queryFn: () =>
      apiGet<{ customers: SageCustomerSearchRow[] }>(
        `${COMMERCE_API_BASE}/customers/search?q=${encodeURIComponent(trimmed)}&limit=25`,
      ),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  return {
    customers: enabled ? query.data?.customers ?? [] : [],
    isLoading: enabled && query.isLoading,
    error: query.error,
  };
}

// --------------------------------------------------------------------------
// Tones + labels
// --------------------------------------------------------------------------

export const ORG_TYPE_TONES: Record<OrgType, string> = {
  wholesaler: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  broker: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
};

export function orgTypeLabel(t: OrgType): string {
  return t === 'wholesaler' ? 'Wholesaler' : 'Broker';
}

export const AGREEMENT_STATUS_TONES: Record<ConnectAgreementStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  signed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  declined: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  voided: 'bg-gray-100 text-gray-500 dark:bg-gray-800/60 dark:text-gray-400',
  expired: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

export function agreementStatusLabel(s: ConnectAgreementStatus): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Most-actionable-first ordering for the cross-partner agreements list.
export const AGREEMENT_STATUS_ORDER: ConnectAgreementStatus[] = [
  'sent',
  'draft',
  'expired',
  'declined',
  'signed',
  'voided',
];

export function agreementStatusRank(s: ConnectAgreementStatus): number {
  const i = AGREEMENT_STATUS_ORDER.indexOf(s);
  return i === -1 ? AGREEMENT_STATUS_ORDER.length : i;
}

export const ORG_STATUS_TONES: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  prospect: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

export function orgStatusTone(status: string): string {
  return ORG_STATUS_TONES[status] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
}

export function connectMoney(v: number | string | null | undefined): string {
  if (v == null || v === '') return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Number formatting for the editable price cells: preserve blank vs 0.
export function priceCellValue(v: number | string | null | undefined): string {
  if (v == null || v === '') return '';
  return String(v);
}

// True when an agreement expires within `days` of now (used for highlight).
export function expiresSoon(iso: string | null, days = 60): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return false;
  const now = Date.now();
  return t >= now && t - now <= days * 24 * 60 * 60 * 1000;
}
